import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于天美影视',
    description: '天美影视高清免费在线观看平台，致力于为用户提供不缓冲不卡顿的流畅追剧体验。',
    url: 'https://www.tianmeiys.com/about',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '天美影视',
    url: 'https://www.tianmeiys.com/',
    description: '天美影视高清免费在线观看平台，不缓冲不卡顿流畅追剧，免费看最新高清电影电视剧全集。',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-8888',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '天美影视需要付费吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '天美影视完全免费，所有高清电影电视剧均可免费在线观看，无需任何付费。',
        },
      },
      {
        '@type': 'Question',
        name: '天美影视播放会卡顿吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '天美影视采用先进的视频加速技术，确保播放流畅不卡顿，无需缓冲等待，带给您极致观影体验。',
        },
      },
      {
        '@type': 'Question',
        name: '天美影视支持哪些设备观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '天美影视支持电脑、平板、手机等多种设备在线观看，随时随地畅享高清影视内容。',
        },
      },
      {
        '@type': 'Question',
        name: '天美影视的内容更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '天美影视每日更新最新电影和电视剧资源，确保用户能第一时间观看到最新上映的影视作品。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系天美影视客服？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过客服热线 400-888-8888 或发送邮件至 service@tianmeiys.com 联系我们。我们的客服团队将在工作时间内尽快回复您。',
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <main className="min-h-screen bg-background-50">
        <Navbar />
        <Breadcrumb />

        <section className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 text-center mb-3">
              关于天美影视
            </h1>
            <p className="text-sm text-foreground-400 text-center mb-12 max-w-2xl mx-auto leading-relaxed">
              天美影视 — 您的高清在线观影首选平台。不缓冲，不卡顿，畅享流畅追剧体验。
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
              <div className="bg-background-100/60 rounded-xl p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-500/10">
                  <i className="ri-movie-2-line text-2xl text-primary-500"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-800 mb-2">海量资源</h3>
                <p className="text-sm text-foreground-400 leading-relaxed">
                  收录数万部高清电影、电视剧、综艺、动漫，覆盖动作、喜剧、爱情、科幻等多种类型，满足不同用户的观影需求。
                </p>
              </div>
              <div className="bg-background-100/60 rounded-xl p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-500/10">
                  <i className="ri-speed-up-line text-2xl text-accent-500"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-800 mb-2">流畅体验</h3>
                <p className="text-sm text-foreground-400 leading-relaxed">
                  采用领先的视频加速与CDN分发技术，确保高清画质下依然流畅不卡顿，无需漫长缓冲等待。
                </p>
              </div>
              <div className="bg-background-100/60 rounded-xl p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-500/10">
                  <i className="ri-device-line text-2xl text-secondary-500"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-800 mb-2">多端适配</h3>
                <p className="text-sm text-foreground-400 leading-relaxed">
                  完美适配电脑、平板、手机等多种设备，无论在家还是出门在外，随时随地开启高清观影之旅。
                </p>
              </div>
            </div>

            <div className="bg-background-100/60 rounded-xl p-8 mb-10">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4">品牌故事</h2>
              <div className="space-y-3 text-sm text-foreground-400 leading-relaxed">
                <p>
                  天美影视创立于2020年，是一家专注于高清影视在线播放的互联网平台。自成立以来，天美影视始终秉持"高清、免费、流畅"的服务理念，致力于为广大影视爱好者提供优质的在线观影体验。
                </p>
                <p>
                  我们的内容库涵盖电影、电视剧、综艺、动漫、纪录片等多种类型，从最新上映的院线大片到经典老片，从热门国产剧到海外佳作，天美影视一站式满足您的观影需求。
                </p>
                <p>
                  天美影视团队由一群热爱影视的技术人才组成，我们不断优化播放技术和用户体验，确保每一位用户都能享受到<strong className="text-foreground-600">天美影视高清免费在线观看</strong>带来的极致流畅体验——<strong className="text-foreground-600">不缓冲不卡顿流畅追剧</strong>，是我们不变的承诺。
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
              <div className="bg-background-100/60 rounded-xl p-8">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4">联系我们</h2>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 shrink-0">
                      <i className="ri-phone-line text-primary-500"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-300 mb-0.5">客服热线</p>
                      <p className="text-sm font-medium text-foreground-700">400-888-8888</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-500/10 shrink-0">
                      <i className="ri-mail-line text-accent-500"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-300 mb-0.5">电子邮箱</p>
                      <p className="text-sm font-medium text-foreground-700">service@tianmeiys.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-500/10 shrink-0">
                      <i className="ri-map-pin-line text-secondary-500"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-300 mb-0.5">公司地址</p>
                      <p className="text-sm font-medium text-foreground-700">北京市朝阳区建国路88号天美大厦</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-100/60 rounded-xl p-8">
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4">常见问题</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-700 mb-1">
                      <i className="ri-question-line text-primary-500 mr-1.5"></i>
                      天美影视需要付费吗？
                    </h3>
                    <p className="text-xs text-foreground-400 leading-relaxed pl-5">
                      天美影视完全免费，所有高清电影电视剧均可免费在线观看，无需任何付费。
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-700 mb-1">
                      <i className="ri-question-line text-primary-500 mr-1.5"></i>
                      播放会卡顿吗？
                    </h3>
                    <p className="text-xs text-foreground-400 leading-relaxed pl-5">
                      采用先进视频加速技术，播放流畅不卡顿，无需缓冲等待，极致观影体验。
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-700 mb-1">
                      <i className="ri-question-line text-primary-500 mr-1.5"></i>
                      支持哪些设备？
                    </h3>
                    <p className="text-xs text-foreground-400 leading-relaxed pl-5">
                      支持电脑、平板、手机等多种设备，随时随地畅享高清影视内容。
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-700 mb-1">
                      <i className="ri-question-line text-primary-500 mr-1.5"></i>
                      内容更新频率如何？
                    </h3>
                    <p className="text-xs text-foreground-400 leading-relaxed pl-5">
                      每日更新最新影视资源，第一时间观看到最新上映的影视作品。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}